import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AnalyticsService,
  EnvironmentService,
  InceptionService,
  TranslationService,
} from '@dch/ng-common';
import { parse } from 'date-fns';
import { MONTH_TRANSLATION_KEYS } from '@dch/billing-business-logic';
import {
  IAggregatorDocument,
  IAggregatorMonth,
} from '../models/aggregator.model';

interface IChartDataItem {
  date: string;
  documentDate: Date;
  positiveAmount?: number;
  negativeAmount?: number;
  positivePercentage?: number;
  negativePercentage?: number;
  isSelected?: boolean;
}

@Component({
  selector: 'my-bill-evolution-chart',
  templateUrl: './evolution-chart.component.html',
  standalone: false,
})
export class EvolutionChartComponent implements OnInit {
  chartTitle: string;
  displayedChartData: IChartDataItem[];
  allChartData: IChartDataItem[];
  maxAmount: number;
  isMobile: boolean;
  isNextArrowDisabled: boolean;
  isPreviousArrowDisabled: boolean;

  @Input() evolutionByMonth?: Array<IAggregatorMonth>;
  @Output() itemClick = new EventEmitter<IAggregatorMonth>();

  constructor(
    private translationService: TranslationService,
    private elementRef: ElementRef,
    private inceptionService: InceptionService,
    private environmentService: EnvironmentService,
    private analytics: AnalyticsService
  ) {}

  ngOnInit() {
    this.initInception();
    this.isMobile = this.environmentService.isMobile;
    this.allChartData = this.evolutionByMonth.map((month) => ({
      date: month.monthDate,
      documentDate: this.getDate(month.monthDate),
      positiveAmount: parseFloat(month.positiveDocuments.totalAmount),
      negativeAmount: parseFloat(month.negativeDocuments.totalAmount),
    }));

    // sort by date to match ascending the order required by the library
    this.allChartData.sort((a, b) =>
      parse(a.documentDate) > parse(b.documentDate) ? 1 : -1
    );

    this.displayedChartData = [...this.allChartData];

    // limit the data for mobile view
    if (this.isMobile) {
      this.displayedChartData = this.allChartData.slice(-6);
    }
    this.setupChart();
  }

  getDate(documentDate: string) {
    const [day, month, year] = documentDate.split('/');
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  private getChartItemPercentage(
    monthItem: IAggregatorDocument,
    maximum: number
  ): number | undefined {
    if (parseFloat(monthItem.totalAmount) === 0) {
      if (monthItem.documents.length === 0) {
        return undefined;
      } else {
        return 1;
      }
    } else {
      const percentage = Math.floor(
        Math.abs(parseFloat(monthItem.totalAmount) / maximum) * 100
      );
      return percentage >= 1 ? percentage : 1;
    }
  }
  private initInception(): void {
    setTimeout(() => {
      this.inceptionService.cssLoad(this.elementRef);
    });
  }

  private setupChart(): void {
    // compute the max amount bar value
    this.maxAmount = Math.max(
      ...this.displayedChartData.map((item) =>
        Math.max(
          isNaN(item.positiveAmount) ? 0 : item.positiveAmount,
          Math.abs(isNaN(item.negativeAmount) ? 0 : item.negativeAmount)
        )
      )
    );
    // compute the percentages
    this.displayedChartData.forEach((item) => {
      const monthItem = this.evolutionByMonth.find(
        (month) => month.monthDate === item.date
      );
      item.positivePercentage = this.getChartItemPercentage(
        monthItem.positiveDocuments,
        this.maxAmount
      );
      item.negativePercentage = this.getChartItemPercentage(
        monthItem.negativeDocuments,
        this.maxAmount
      );
    });
    // set the last item as selected
    this.selectChartItem(this.displayedChartData.length - 1);
    // set the chart title
    const startDate = this.displayedChartData[0].date.split('/');
    const endDate =
      this.displayedChartData[this.displayedChartData.length - 1].date.split(
        '/'
      );
    const startDateYear = startDate[2];
    const endDateYear = endDate[2];
    this.chartTitle = `${this.getMonthLabel(
      startDate[1]
    )} ${startDateYear} - ${this.getMonthLabel(endDate[1])} ${endDateYear}`;
    // set the arrows state
    this.isNextArrowDisabled =
      this.allChartData.findIndex(
        (item) =>
          item.date ===
          this.displayedChartData[this.displayedChartData.length - 1].date
      ) ===
      this.allChartData.length - 1;
    this.isPreviousArrowDisabled =
      this.allChartData.findIndex(
        (item) => item.date === this.displayedChartData[0].date
      ) === 0;
  }

  getMonthLabel(monthString: string): string {
    const monthNumber: number =
      monthString && Number.isInteger(+monthString)
        ? parseInt(monthString, 10) - 1
        : 0;
    return this.translationService
      .getTranslatedValueFor(MONTH_TRANSLATION_KEYS[monthNumber])
      .substr(0, 3);
  }

  selectChartItem(itemIndex: number, hasUserClicked = false): void {
    this.displayedChartData.forEach((item) => (item.isSelected = false));
    this.displayedChartData[itemIndex].isSelected = true;
    const monthItem = this.evolutionByMonth.find(
      (month) => month.monthDate === this.displayedChartData[itemIndex].date
    );

    this.itemClick.emit(monthItem);
    if (hasUserClicked) {
      this.analytics.reportEvent({
        event: 'button-click',
        popup: {
          zone: 'mybill.evolution',
          name: 'evolution detail',
        },
      });
    }
  }

  handleArrowClick(direction: 'next' | 'previous'): void {
    if (direction === 'next') {
      this.displayedChartData = this.allChartData.slice(6, 12);
    } else {
      this.displayedChartData = this.allChartData.slice(0, 6);
    }
    this.setupChart();
  }
}
